'use client';

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { cn } from '@/hen-ui/utils';
import type {
  NavItem,
  SubNavColumnWidth,
  SubNavPanelStyle,
  SubNavCta,
  SubNavMenuTransition,
} from './types';
import { DesktopSidebar } from './DesktopSidebar';
import { DesktopContentPanel } from './DesktopContentPanel';
import { SubNavFooter } from './SubNavFooter';
import { hasSubNavCtaMessage } from './utils';

interface DesktopLayoutProps {
  title: string;
  sections: NavItem[];
  cta?: SubNavCta;
  dividedLastColumn?: boolean;
  menuContent?: ReactNode;
  showHeading?: boolean;
  menuTransition?: SubNavMenuTransition;
  columnWidth?: SubNavColumnWidth;
  panelStyle?: SubNavPanelStyle;
}

export function DesktopLayout({
  title,
  sections,
  cta,
  dividedLastColumn = false,
  menuContent,
  showHeading,
  menuTransition = 'none',
  columnWidth,
  panelStyle,
}: DesktopLayoutProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [sections]);

  const handleActiveChange = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  return (
    <div className='hidden min-h-subnav-panel md:flex'>
      <DesktopSidebar
        title={title}
        sections={sections}
        activeIndex={activeIndex}
        onActiveChange={handleActiveChange}
      />

      <div className='flex flex-1 flex-col'>
        <div className='grid flex-1'>
          {sections.map((section, sectionIdx) => {
            const isActive = sectionIdx === activeIndex;
            const sectionColumns = section.children ?? [];

            if (sectionColumns.length === 0) return null;

            return (
              <div
                key={section.id}
                aria-hidden={!isActive}
                {...(!isActive ? { inert: 'true' as unknown as boolean } : {})}
                className={cn(
                  'col-start-1 row-start-1 w-full',
                  isActive
                    ? 'visible z-10 opacity-100'
                    : 'pointer-events-none invisible z-0 opacity-0',
                  // The outgoing pane keeps `visible` until its fade ends;
                  // `inert` and `aria-hidden` still switch at once.
                  menuTransition === 'fade' &&
                    'transition-[opacity,visibility] duration-normal motion-reduce:transition-none',
                )}
              >
                <DesktopContentPanel
                  heading={section.label}
                  columns={sectionColumns}
                  dividedLastColumn={dividedLastColumn}
                  menu={title}
                  menuContent={isActive ? menuContent : undefined}
                  showHeading={showHeading}
                  columnWidth={columnWidth}
                  panelStyle={panelStyle}
                />
              </div>
            );
          })}
        </div>

        {hasSubNavCtaMessage(cta) && <SubNavFooter cta={cta} />}
      </div>
    </div>
  );
}
